import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type {
  SearchOptionsResponseDto,
  TeamsResponseDto,
  TeamsSduiResponseDto,
} from '@nomiss/sdk';
import {
  getTeamsByLeagueCodes,
  getTeamsSdui,
  searchTeams,
} from '@nomiss/services';

export const TEAMS_LEAGUE_PAGE_SIZE = 2;

export const teamsQueryKeys = {
  byLeagueCodes: (leagueCodes: string[]) =>
    ['teams', 'by-league-codes', leagueCodes] as const,
  byLeagueCodesInfinite: (leagueCodes: string[], pageSize: number) =>
    ['teams', 'by-league-codes-infinite', leagueCodes, pageSize] as const,
  sdui: (sportCode: string, leagueCodes: string[]) =>
    ['teams', 'sdui', sportCode, leagueCodes] as const,
  search: (
    sportCode: string,
    leagueCodes: string[],
    query: string,
    limit: number,
  ) => ['teams', 'search', sportCode, leagueCodes, query, limit] as const,
};

/**
 * `/v1/teams` rejects an empty `leagueCodes` list with a 400, and imperative
 * fetches (`fetchNextPage`) bypass `enabled`, so empty batches are resolved
 * locally instead of reaching the network.
 */
const EMPTY_TEAMS_RESPONSE: TeamsResponseDto = {
  status: 'Success',
  message: 'No leagues selected',
  data: [],
};

export const useTeamsByLeagueCodes = (
  leagueCodes: Array<string>,
  options?: { enabled?: boolean },
) => {
  const teamsByLeagueCodesQuery = useQuery<TeamsResponseDto>({
    queryKey: teamsQueryKeys.byLeagueCodes(leagueCodes),
    queryFn: () =>
      leagueCodes.length === 0
        ? Promise.resolve(EMPTY_TEAMS_RESPONSE)
        : getTeamsByLeagueCodes(leagueCodes),
    ...options,
  });

  return { teamsByLeagueCodesQuery };
};

/**
 * Loads teams for selected leagues in batches of `pageSize` (default 2).
 * Call `fetchNextPage` when the user scrolls near the end.
 */
export const useTeamsByLeagueCodesInfinite = (
  leagueCodes: Array<string>,
  options?: { enabled?: boolean; pageSize?: number },
) => {
  const pageSize = options?.pageSize ?? TEAMS_LEAGUE_PAGE_SIZE;
  const enabled = (options?.enabled ?? true) && leagueCodes.length > 0;

  const teamsByLeagueCodesInfiniteQuery = useInfiniteQuery<TeamsResponseDto>({
    queryKey: teamsQueryKeys.byLeagueCodesInfinite(leagueCodes, pageSize),
    queryFn: ({ pageParam }) => {
      const start = pageParam as number;
      const batch = leagueCodes.slice(start, start + pageSize);
      if (batch.length === 0) {
        return Promise.resolve(EMPTY_TEAMS_RESPONSE);
      }
      return getTeamsByLeagueCodes(batch);
    },
    initialPageParam: 0,
    getNextPageParam: (_lastPage, _pages, lastPageParam) => {
      const next = (lastPageParam as number) + pageSize;
      return next < leagueCodes.length ? next : undefined;
    },
    enabled,
  });

  return { teamsByLeagueCodesInfiniteQuery };
};

export const useTeamsSdui = (
  sportCode: string,
  leagueCodes: Array<string>,
  options?: { enabled?: boolean },
) => {
  const teamsSduiQuery = useQuery<TeamsSduiResponseDto>({
    queryKey: teamsQueryKeys.sdui(sportCode, leagueCodes),
    queryFn: () => getTeamsSdui(sportCode, leagueCodes),
    ...options,
  });

  return { teamsSduiQuery };
};

export const useTeamsSearch = (
  sportCode: string,
  leagueCodes: Array<string>,
  query: string,
  options?: { enabled?: boolean; limit?: number },
) => {
  const limit = options?.limit ?? 20;
  const enabled =
    (options?.enabled ?? true) &&
    sportCode.length > 0 &&
    leagueCodes.length > 0 &&
    query.trim().length > 0;

  const teamsSearchQuery = useQuery<SearchOptionsResponseDto>({
    queryKey: teamsQueryKeys.search(sportCode, leagueCodes, query, limit),
    queryFn: () =>
      searchTeams({
        sportCode,
        leagueCodes,
        query: query.trim(),
        limit,
      }),
    enabled,
  });

  return { teamsSearchQuery };
};
